import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Settings, 
  Save,
  RefreshCw,
  Lock,
  User,
  Mail,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  FileText,
  Smartphone,
  ExternalLink,
  LifeBuoy,
  Bug
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function AdminSettings() {
  const { admin } = useOutletContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState({
    name: '',
    email: ''
  });
  
  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Support settings state
  const [supportSettings, setSupportSettings] = useState({
    support_email: 'support@connect-us.co.nz',
    bug_report_email: 'bugs@connect-us.co.nz'
  });
  const [savingSupport, setSavingSupport] = useState(false);

  useEffect(() => {
    if (admin) {
      setProfile({
        name: admin.name || '',
        email: admin.email || ''
      });
    }
    // Fetch support settings
    fetchSupportSettings();
  }, [admin]);

  const fetchSupportSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API_URL}/api/settings/support`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setSupportSettings(res.data);
      }
    } catch (error) {
      console.log('Using default support settings');
    }
  };

  const handleSaveSupportSettings = async () => {
    setSavingSupport(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/settings/support`, supportSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Support settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update support settings');
    } finally {
      setSavingSupport(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local storage
      const updatedAdmin = { ...admin, ...profile };
      localStorage.setItem('admin', JSON.stringify(updatedAdmin));
      
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);
    
    // Validation
    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }
    
    setSavingPassword(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPasswordSuccess(true);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      toast.success('Password changed successfully');
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Settings</h1>
        <p className="text-slate-400 mt-1">
          Manage your admin account settings
        </p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Profile Settings */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Profile Information
            </CardTitle>
            <CardDescription className="text-slate-400">
              Update your admin profile details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Username</Label>
              <Input
                className="bg-slate-900/50 border-slate-600 text-slate-400"
                value={admin?.username || ''}
                disabled
              />
              <p className="text-xs text-slate-500">Username cannot be changed</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Display Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  placeholder="Your name"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  placeholder="admin@example.com"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                />
              </div>
            </div>
            
            <div className="flex justify-end pt-2">
              <Button 
                onClick={handleSaveProfile} 
                disabled={savingProfile}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {savingProfile ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Profile
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-400" />
              Change Password
            </CardTitle>
            <CardDescription className="text-slate-400">
              Update your admin password for security
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {passwordError && (
                <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{passwordError}</AlertDescription>
                </Alert>
              )}
              
              {passwordSuccess && (
                <Alert className="bg-green-500/10 border-green-500/30 text-green-400">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>Password changed successfully!</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label className="text-slate-300">Current Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    className="pl-10 pr-10 bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Enter current password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    onClick={() => togglePasswordVisibility('current')}
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    className="pl-10 pr-10 bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Enter new password (min 8 characters)"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={8}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    onClick={() => togglePasswordVisibility('new')}
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    className="pl-10 pr-10 bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Confirm new password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    onClick={() => togglePasswordVisibility('confirm')}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-end pt-2">
                <Button 
                  type="submit"
                  disabled={savingPassword}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  {savingPassword ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                  Change Password
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Security Info */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <Settings className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-medium text-white mb-1">Security Recommendations</h3>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• Use a strong password with at least 8 characters</li>
                  <li>• Include a mix of letters, numbers, and symbols</li>
                  <li>• Change your password regularly</li>
                  <li>• Never share your admin credentials</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support Email Settings */}
        <Card className="bg-slate-800/50 border-slate-700" data-testid="support-settings-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-green-400" />
              Support Settings
            </CardTitle>
            <CardDescription className="text-slate-400">
              Configure support and bug report email addresses displayed to users
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Support Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  placeholder="support@example.com"
                  value={supportSettings.support_email}
                  onChange={(e) => setSupportSettings({ ...supportSettings, support_email: e.target.value })}
                  data-testid="support-email-input"
                />
              </div>
              <p className="text-xs text-slate-500">General support inquiries from users</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Bug Report Email</Label>
              <div className="relative">
                <Bug className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                  placeholder="bugs@example.com"
                  value={supportSettings.bug_report_email}
                  onChange={(e) => setSupportSettings({ ...supportSettings, bug_report_email: e.target.value })}
                  data-testid="bug-report-email-input"
                />
              </div>
              <p className="text-xs text-slate-500">Technical issues and bug reports</p>
            </div>
            
            <div className="flex justify-end pt-2">
              <Button 
                onClick={handleSaveSupportSettings}
                disabled={savingSupport}
                className="bg-green-500 hover:bg-green-600"
              >
                {savingSupport ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Support Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Developer Resources */}
        <Card className="bg-slate-800/50 border-slate-700" data-testid="developer-resources-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Developer Resources
            </CardTitle>
            <CardDescription className="text-slate-400">
              Documentation and handoff packages for development teams
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Mobile App Handoff */}
            <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-medium text-white">Mobile App Developer Handoff</h3>
                  <a 
                    href="/docs/mobile-handoff/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-md text-sm font-medium transition-colors"
                  >
                    Open Documentation Portal
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <p className="text-sm text-slate-400 mb-3">
                  Complete documentation for building the Expo/React Native mobile companion app.
                  Includes API specs, authentication flows, SIP configuration, push notifications, and brand assets.
                </p>
                <div className="flex flex-wrap gap-2">
                  <a 
                    href="/docs/mobile-handoff/README.md" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <FileText className="w-4 h-4" />
                    README
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a 
                    href="/docs/mobile-handoff/01-API-SPECIFICATION.md" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <FileText className="w-4 h-4" />
                    API Spec
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a 
                    href="/docs/mobile-handoff/02-AUTHENTICATION.md" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <FileText className="w-4 h-4" />
                    Auth Flow
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a 
                    href="/docs/mobile-handoff/03-SIP-CONFIGURATION.md" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <FileText className="w-4 h-4" />
                    SIP Config
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a 
                    href="/docs/mobile-handoff/04-PUSH-NOTIFICATIONS.md" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <FileText className="w-4 h-4" />
                    Push Notifications
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a 
                    href="/docs/mobile-handoff/05-BRAND-ASSETS.md" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    <FileText className="w-4 h-4" />
                    Brand Assets
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
